import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toStorySlug } from '../../../lib/slugify';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

export interface Story {
  id: string;
  title: string;
  genre: string;
  content: string;
  cover_image: string | null;
  like_count: number;
  read_count: number;
  published_date: string;
  profiles: {
    display_name: string;
  };
}

interface StoryCardProps {
  story: Story;
  index: number;
}

const StoryCard = ({ story, index }: StoryCardProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likeLoading, setLikeLoading] = useState(false);
  const [commentCount, setCommentCount] = useState<number | null>(null);
  const [avgRating, setAvgRating] = useState<number | null>(null);

  useEffect(() => {
    const fetchCounts = async () => {
      // Fetch real like count from story_likes table
      const { count: likes } = await supabase
        .from('story_likes')
        .select('id', { count: 'exact', head: true })
        .eq('story_id', story.id);
      setLikeCount(likes ?? 0);

      // Fetch comment count
      const { count: comments } = await supabase
        .from('comments')
        .select('id', { count: 'exact', head: true })
        .eq('story_id', story.id);
      if (comments !== null) setCommentCount(comments);

      // Fetch average star rating via secure aggregate function (bypasses RLS)
      const { data: ratingData } = await supabase
        .rpc('get_story_avg_rating', { p_story_id: story.id });
      if (ratingData && ratingData.length > 0 && ratingData[0].rating_count > 0) {
        setAvgRating(Number(ratingData[0].avg_rating));
      } else {
        setAvgRating(null);
      }
    };

    fetchCounts();
  }, [story.id]);

  useEffect(() => {
    const checkLiked = async () => {
      if (!user) { setLiked(false); return; }
      const { data } = await supabase
        .from('story_likes')
        .select('id')
        .eq('user_id', user.id)
        .eq('story_id', story.id)
        .maybeSingle();
      setLiked(!!data);
    };
    checkLiked();
  }, [user, story.id]);

  const handleVote = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      navigate('/auth/login');
      return;
    }
    if (likeLoading) return;
    setLikeLoading(true);

    try {
      if (liked) {
        await supabase
          .from('story_likes')
          .delete()
          .eq('user_id', user.id)
          .eq('story_id', story.id);
        setLiked(false);
        setLikeCount((prev) => Math.max(0, prev - 1));
      } else {
        await supabase
          .from('story_likes')
          .insert({ user_id: user.id, story_id: story.id });
        setLiked(true);
        setLikeCount((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      setLikeLoading(false);
    }
  };

  const getExcerpt = (content: string) => {
    const plainText = content.replace(/<[^>]*>/g, '');
    return plainText.length > 150 ? plainText.slice(0, 150) + '...' : plainText;
  };

  const getReadTime = (content: string) => {
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  };

  const getTags = (genre: string) => {
    const tagMap: Record<string, string[]> = {
      'Psychological Horror': ['psychological', 'mind-bending', 'suspense'],
      'Supernatural': ['supernatural', 'paranormal', 'ghost'],
      'Cosmic Dread': ['cosmic', 'lovecraftian', 'existential'],
      'Urban Legends': ['urban', 'folklore', 'legend'],
      'Slasher': ['slasher', 'thriller', 'survival'],
      'Body Horror': ['body horror', 'grotesque', 'visceral'],
      'Gothic': ['gothic', 'dark', 'atmospheric'],
    };
    return tagMap[genre] || ['horror', 'dark', 'suspense'];
  };

  return (
    <Link
      to={`/story/${toStorySlug(story.title, story.id)}`}
      className="group bg-[#0f0f0f] border border-red-900/20 rounded-lg overflow-hidden hover:border-red-600/50 transition-all duration-300 hover:shadow-2xl hover:shadow-red-900/20 cursor-pointer flex flex-col"
      style={{ animation: `fadeInUp 0.5s ease-out ${(index % 9) * 0.06}s both` }}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden shrink-0">
        <img
          src={story.cover_image || `https://public.readdy.ai/ai/img_res/aa1aa309d6e1f72be55f3b2f4091fb85.jpg${story.id}&orientation=landscape`}
          alt={story.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f] via-transparent to-transparent" />

        <span className="absolute top-3 left-3 px-2.5 py-1 bg-red-600/90 backdrop-blur-sm text-white text-xs rounded font-['Crimson_Text'] whitespace-nowrap">
          {story.genre}
        </span>
        <span className="absolute top-3 right-3 px-2.5 py-1 bg-black/70 backdrop-blur-sm text-gray-300 text-xs rounded font-['Crimson_Text'] whitespace-nowrap">
          <i className="ri-time-line mr-1" />
          {getReadTime(story.content)}
        </span>
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-lg font-bold text-white mb-1 font-['Cinzel'] group-hover:text-red-500 transition-colors line-clamp-2 leading-snug">
          {story.title}
        </h3>

        <p className="text-gray-500 text-xs mb-3 font-['Crimson_Text']">
          by <span className="text-red-500">{story.profiles.display_name}</span>
          <span className="mx-2 text-gray-700">·</span>
          {new Date(story.published_date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>

        <p className="text-gray-400 text-sm font-['Crimson_Text'] leading-relaxed line-clamp-3 flex-1">
          {getExcerpt(story.content)}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {getTags(story.genre).slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="text-xs text-gray-500 bg-black/50 border border-red-900/10 px-2 py-0.5 rounded font-['Crimson_Text']"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 mt-4 border-t border-red-900/20">
          <div className="flex items-center gap-4">
            <button
              onClick={handleVote}
              className={`flex items-center gap-1 transition-colors cursor-pointer ${
                liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
              }`}
            >
              <i className={liked ? 'ri-heart-fill' : 'ri-heart-line'} />
              <span className="text-sm font-['Crimson_Text']">{likeCount.toLocaleString()}</span>
            </button>
            <span className="flex items-center gap-1 text-gray-500 text-sm font-['Crimson_Text']">
              <i className="ri-chat-3-line" />
              {commentCount !== null ? commentCount.toLocaleString() : '—'}
            </span>
            {avgRating !== null && (
              <span className="flex items-center gap-1 text-yellow-500 text-sm font-['Crimson_Text']">
                <i className="ri-star-fill" />
                {avgRating.toFixed(1)}
              </span>
            )}
          </div>
          <span className="text-red-600 text-sm font-['Crimson_Text'] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 whitespace-nowrap">
            Read <i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default StoryCard;
